import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["trigger", "menu", "scrollContent", "scrollBar", "item", "triggerText", "openIcon", "closeIcon"]
  static values = {
    open: { type: Boolean, default: false },
    portal: { type: Boolean, default: false },
    position: { type: String, default: "bottom_right" }
  }

  connect() {
    // Bind click outside handler
    this.clickOutsideHandler = this.clickOutside.bind(this)
    this.keydownHandler = this.handleKeydown.bind(this)
    this.submitHandler = this.close.bind(this)
    this.turboBeforeCacheHandler = this.close.bind(this)
    this.pageShowHandler = this.handlePageShow.bind(this)

    document.addEventListener("click", this.clickOutsideHandler)
    document.addEventListener("keydown", this.keydownHandler)
    document.addEventListener("turbo:before-cache", this.turboBeforeCacheHandler)
    window.addEventListener("pageshow", this.pageShowHandler)

    // For portal mode, move the menu to body to escape stacking contexts
    if (this.portalValue && this.hasMenuTarget) {
      // Store reference before moving
      this.portalMenuElement = this.menuTarget
      this.originalParent = this.portalMenuElement.parentNode
      this.originalNextSibling = this.portalMenuElement.nextSibling
      // Ensure it's hidden before moving to body
      this.portalMenuElement.classList.add('hidden')
      this.portalMenuElement.setAttribute('hidden', '')
      document.body.appendChild(this.portalMenuElement)

      // Listen for turbo events and modal close to hide the dropdown
      this.turboBeforeVisitHandler = this.close.bind(this)
      document.addEventListener("turbo:before-visit", this.turboBeforeVisitHandler)

      // Use MutationObserver to detect when original parent is hidden/removed
      this.setupParentObserver()
    }

    // Portal menus live outside the controller element, so Stimulus actions on
    // their forms cannot resolve the dropdown controller through DOM ancestry.
    // Listen on the menu element directly to close it before a form navigates.
    if (this.hasMenuTarget || this.portalMenuElement) {
      this.getMenuElement().addEventListener("submit", this.submitHandler)
    }

    this.updateVisibility()
    this.initScrollBehavior()
  }

  setupParentObserver() {
    if (!this.originalParent) return

    // Find the closest modal or dialog ancestor
    this.modalAncestor = this.originalParent.closest('[data-controller="modal"]')
    if (this.modalAncestor) {
      this.mutationObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            // Check if modal container is hidden
            const container = this.modalAncestor.querySelector('[data-modal-target="container"]')
            if (container && container.classList.contains('hidden')) {
              this.close()
            }
          }
        }
      })

      const container = this.modalAncestor.querySelector('[data-modal-target="container"]')
      if (container) {
        this.mutationObserver.observe(container, { attributes: true, attributeFilter: ['class'] })
      }
    }
  }

  // Get menu element - use stored reference for portal mode
  getMenuElement() {
    return this.portalMenuElement || this.menuTarget
  }

  initScrollBehavior() {
    if (!this.hasScrollContentTarget || !this.hasScrollBarTarget) return

    this.scrollHandler = this.updateScrollBar.bind(this)
    this.scrollContentTarget.addEventListener("scroll", this.scrollHandler)

    // Initial update
    this.updateScrollBar()
  }

  updateScrollBar() {
    if (!this.hasScrollContentTarget || !this.hasScrollBarTarget) return

    const content = this.scrollContentTarget
    const scrollBar = this.scrollBarTarget
    const scrollContainer = scrollBar.parentElement.parentElement

    // Hide scrollbar if content doesn't overflow
    if (content.scrollHeight <= content.clientHeight) {
      scrollContainer.style.display = "none"
      return
    } else {
      scrollContainer.style.display = "flex"
    }

    const atTop = content.scrollTop <= 0

    // Delayed hide of scrollbar thumb when at very top
    if (atTop) {
      // Show immediately, then schedule hide
      scrollBar.style.opacity = "1"

      if (this.scrollHideTimeout) {
        clearTimeout(this.scrollHideTimeout)
      }

      this.scrollHideTimeout = setTimeout(() => {
        // Ensure still valid and still at top before hiding
        if (!this.hasScrollContentTarget || !this.hasScrollBarTarget) return
        if (this.scrollContentTarget.scrollTop <= 0) {
          this.scrollBarTarget.style.opacity = "0"
        }
        this.scrollHideTimeout = null
      }, 500)
    } else {
      // When user scrolls down, cancel any pending hide and show thumb
      if (this.scrollHideTimeout) {
        clearTimeout(this.scrollHideTimeout)
        this.scrollHideTimeout = null
      }
      scrollBar.style.opacity = "1"
    }

    const scrollRatio = content.scrollTop / (content.scrollHeight - content.clientHeight)
    const barHeight = (content.clientHeight / content.scrollHeight) * 100
    const barTop = scrollRatio * (100 - barHeight)

    scrollBar.style.height = `${barHeight}%`
    scrollBar.style.top = `${barTop}%`
    scrollBar.style.bottom = 'auto'
  }

  disconnect() {
    document.removeEventListener("click", this.clickOutsideHandler)
    document.removeEventListener("keydown", this.keydownHandler)
    document.removeEventListener("turbo:before-cache", this.turboBeforeCacheHandler)
    window.removeEventListener("pageshow", this.pageShowHandler)

    const menu = this.getMenuElement()
    if (menu && this.submitHandler) {
      menu.removeEventListener("submit", this.submitHandler)
    }

    if (this.hasScrollContentTarget && this.scrollHandler) {
      this.scrollContentTarget.removeEventListener("scroll", this.scrollHandler)
    }

    // Clean up portal mode resources
    if (this.portalValue) {
      if (this.turboBeforeVisitHandler) {
        document.removeEventListener("turbo:before-visit", this.turboBeforeVisitHandler)
      }
      if (this.mutationObserver) {
        this.mutationObserver.disconnect()
      }

      // Move the menu back to its original position
      if (this.portalMenuElement && this.originalParent) {
        if (this.originalNextSibling) {
          this.originalParent.insertBefore(this.portalMenuElement, this.originalNextSibling)
        } else {
          this.originalParent.appendChild(this.portalMenuElement)
        }
      }
    }
  }

  toggle(event) {
    event.stopPropagation()
    event.preventDefault()
    this.openValue = !this.openValue
    this.updateVisibility()
  }

  stopPropagation(event) {
    event.stopPropagation()
    event.preventDefault()
  }

  open() {
    this.openValue = true
    this.updateVisibility()
  }

  close() {
    this.openValue = false
    this.updateVisibility()
  }

  handlePageShow(event) {
    if (event.persisted) this.close()
  }

  hover(event) {
    const item = event.currentTarget
    const index = this.itemTargets.indexOf(item)
    if (index === -1) return
    this.highlightItem(index)
  }

  select(event) {
    const item = event.currentTarget
    const value = item.dataset.value
    const text = item.dataset.text || item.textContent.trim()

    // Update trigger text if target exists
    if (this.hasTriggerTextTarget) {
      this.triggerTextTarget.textContent = text
    }

    // Dispatch custom event with selected value
    this.element.dispatchEvent(new CustomEvent("dropdown:select", {
      detail: { value, text },
      bubbles: true
    }))

    // Update selected state on items
    this.itemTargets.forEach(i => {
      i.classList.remove("bg-[#fafafb]")
      const checkIcon = i.querySelector("[data-check-icon]")
      if (checkIcon) checkIcon.classList.add("invisible")
    })

    item.classList.add("bg-[#fafafb]")
    const checkIcon = item.querySelector("[data-check-icon]")
    if (checkIcon) checkIcon.classList.remove("invisible")

    this.close()
  }

  clickOutside(event) {
    if (!this.openValue) return

    const menu = this.getMenuElement()
    const clickedInsideController = this.element.contains(event.target)
    const clickedInsideMenu = menu && menu.contains(event.target)

    if (!clickedInsideController && !clickedInsideMenu) {
      this.close()
    }
  }

  handleKeydown(event) {
    if (!this.openValue) return

    switch (event.key) {
      case "Escape":
        event.preventDefault()
        this.close()
        if (this.hasTriggerTarget) {
          this.triggerTarget.focus()
        }
        break
      case "ArrowDown":
        event.preventDefault()
        this.focusNextItem()
        break
      case "ArrowUp":
        event.preventDefault()
        this.focusPreviousItem()
        break
      case "Enter":
        event.preventDefault()
        this.selectHighlightedItem()
        break
      case "Tab":
        // Allow tab to close dropdown naturally
        this.close()
        break
    }
  }

  // Focus management for keyboard navigation
  focusNextItem() {
    const items = this.itemTargets
    if (items.length === 0) return

    const currentIndex = items.findIndex(item => item.classList.contains("bg-[#fafafb]") && !item.dataset.selected)
    const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0
    this.highlightItem(nextIndex)
  }

  focusPreviousItem() {
    const items = this.itemTargets
    if (items.length === 0) return

    const currentIndex = items.findIndex(item => item.classList.contains("bg-[#fafafb]") && !item.dataset.selected)
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1
    this.highlightItem(prevIndex)
  }

  highlightItem(index) {
    const items = this.itemTargets

    // Remove hover state from all items (except selected or disabled)
    items.forEach(item => {
      if (!item.dataset.selected && !item.classList.contains("cursor-not-allowed")) {
        item.classList.remove("bg-[#fafafb]")
      }
    })

    // Add hover state to current item (skip disabled)
    if (items[index] && !items[index].classList.contains("cursor-not-allowed")) {
      items[index].classList.add("bg-[#fafafb]")
      items[index].scrollIntoView({ block: "nearest" })
      this.highlightedIndex = index
    }
  }

  selectHighlightedItem() {
    const items = this.itemTargets
    if (this.highlightedIndex !== undefined && items[this.highlightedIndex]) {
      const item = items[this.highlightedIndex]
      const value = item.dataset.value
      const text = item.dataset.text || item.textContent.trim()

      // Update trigger text if target exists
      if (this.hasTriggerTextTarget) {
        this.triggerTextTarget.textContent = text
      }

      // Dispatch custom event
      this.element.dispatchEvent(new CustomEvent("dropdown:select", {
        detail: { value, text },
        bubbles: true
      }))

      // Update selected state
      items.forEach(i => {
        delete i.dataset.selected
        const checkIcon = i.querySelector("[data-check-icon]")
        if (checkIcon) checkIcon.classList.add("invisible")
      })

      item.dataset.selected = "true"
      const checkIcon = item.querySelector("[data-check-icon]")
      if (checkIcon) checkIcon.classList.remove("invisible")

      this.close()
    }
  }

  getFocusableItems() {
    if (!this.hasMenuTarget) return []
    return Array.from(
      this.menuTarget.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter(el => !el.disabled && el.offsetParent !== null)
  }

  updateVisibility() {
    const menu = this.getMenuElement()
    if (!menu) return

    if (this.openValue) {
      menu.removeAttribute("hidden")
      menu.classList.remove("hidden")

      // Update aria attributes
      if (this.hasTriggerTarget) {
        this.triggerTarget.setAttribute("aria-expanded", "true")
      }

      if (this.hasOpenIconTarget) {
        this.openIconTarget.classList.remove("hidden")
      }
      if (this.hasCloseIconTarget) {
        this.closeIconTarget.classList.add("hidden")
      }

      // Position the menu if in portal mode
      if (this.portalValue) {
        this.positionPortalMenu()
      }
    } else {
      menu.setAttribute("hidden", "")
      menu.classList.add("hidden")

      if (this.hasTriggerTarget) {
        this.triggerTarget.setAttribute("aria-expanded", "false")
      }

      if (this.hasOpenIconTarget) {
        this.openIconTarget.classList.add("hidden")
      }
      if (this.hasCloseIconTarget) {
        this.closeIconTarget.classList.remove("hidden")
      }
    }
  }

  positionPortalMenu() {
    const menu = this.getMenuElement()
    if (!this.hasTriggerTarget || !menu) return

    const triggerRect = this.triggerTarget.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const viewportWidth = window.innerWidth
    const gap = 8 // 8px gap between trigger and menu
    const padding = 16 // Minimum padding from viewport edges

    // Need to get menu dimensions after it's visible
    const menuRect = menu.getBoundingClientRect()
    const menuHeight = menuRect.height
    const menuWidth = menuRect.width

    let top, left

    // Calculate vertical position
    const spaceBelow = viewportHeight - triggerRect.bottom - gap
    const spaceAbove = triggerRect.top - gap

    // Prefer positioning below, but flip to above if not enough space
    if (this.positionValue.startsWith("top") || (spaceBelow < menuHeight && spaceAbove > spaceBelow)) {
      // Position above
      top = triggerRect.top - menuHeight - gap
    } else {
      // Position below
      top = triggerRect.bottom + gap
    }

    console.log('positionPortalMenu called', { menuWidth, menuHeight, viewportWidth, viewportHeight, triggerRect })

    // For wide menus (like date range pickers), always center in viewport
    if (menuWidth > 500) {
      left = Math.max(padding, (viewportWidth - menuWidth) / 2)
      console.log('Wide menu, centering. left =', left)
    } else {
      // Calculate horizontal position based on position value
      if (this.positionValue.includes("left")) {
        left = triggerRect.left
      } else if (this.positionValue.includes("center")) {
        left = triggerRect.left + (triggerRect.width / 2) - (menuWidth / 2)
      } else {
        // Default to right alignment
        left = triggerRect.right - menuWidth
      }

      // Ensure menu stays within viewport bounds with padding
      if (left < padding) {
        left = padding
      } else if (left + menuWidth > viewportWidth - padding) {
        left = viewportWidth - menuWidth - padding
      }
    }

    // For vertical positioning
    if (top < padding) {
      top = padding
    } else if (top + menuHeight > viewportHeight - padding) {
      top = viewportHeight - menuHeight - padding
    }

    // Final safety check - ensure left is at least padding
    if (left < padding) {
      left = padding
    }

    // Apply styles with !important to override any CSS
    menu.style.setProperty('top', `${top}px`, 'important')
    menu.style.setProperty('left', `${left}px`, 'important')
    menu.style.setProperty('right', 'auto', 'important')
    menu.style.setProperty('transform', 'none', 'important')
  }
}
